/**
 * WordPracticeModal
 *
 * Shared modal surface for "click a weak word → record it again" used by
 * both the course-level ShadowingPage and the vocabulary-book speaking
 * practice page (FlashcardPracticePage). The modal owns nothing about
 * the assessment — the host page passes audio recorder state in and
 * receives lifecycle callbacks (start / stop / cancel / play dict
 * audio / retry / dismiss) — so behaviour stays consistent across
 * surfaces while every page keeps its own assessor + score-update
 * plumbing.
 *
 * Lifecycle states the modal visualises:
 *   1. Pre-record   — `result` is undefined, recorder idle. Mic button.
 *   2. Recording    — `isRecording` true. Stop button.
 *   3. Assessing    — `isAssessing` true. Spinner.
 *   4. Result       — `result` defined. Shows the per-word and
 *                     sentence-level deltas (before → after) with a
 *                     count-up animation, plus 「再試一次 / 完成」 CTAs.
 *
 * Dismiss: ESC, backdrop click, or the X button. Always routed
 * through `onCancel` so the parent can clean up recorder + result
 * state in one place. The X is enabled in every state except while
 * the assessor is in flight (we'd orphan the score).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoopyAvatar, { LoopyEmotion } from '../loopy/LoopyAvatar';
import './WordPracticeModal.css';

export interface WordPracticeResult {
  /** Per-word score on the previous attempt (0 for the first retry). */
  wordBefore: number;
  /** Per-word score on the take we just assessed. */
  wordAfter: number;
  /** Sentence-level overall before → after. */
  overallBefore?: number;
  overallAfter?: number;
  /** Sentence-level accuracy before → after. */
  accuracyBefore?: number;
  accuracyAfter?: number;
}

export interface WordPracticeModalProps {
  word: string;
  /** Score the word had on the most recent sentence-level attempt — shown so
   *  the learner sees what they're trying to beat. */
  originalScore: number;
  /** Optional IPA, surfaced as `/<phonetic>/` next to the word. */
  phonetic?: string | null;
  /** Optional dictionary audio URL — shows a play button when present. */
  audioUrl?: string | null;
  isRecording: boolean;
  isAssessing: boolean;
  isPlayingAudio: boolean;
  /** When set, the modal switches to result view. */
  result?: WordPracticeResult;
  onStartRecord: () => void;
  onStopRecord: () => void;
  onPlayAudio: () => void;
  /** Clear the result + recording, return to pre-record state so the
   *  learner can take another shot without closing the modal. */
  onRetry: () => void;
  onCancel: () => void;
}

/**
 * Counts smoothly from `from` → `to` over `duration` ms.
 * Respects `prefers-reduced-motion`: snaps directly to `to` when the
 * user asked for less motion. Lives inside the modal file because
 * nothing else in the app needs an animated number yet.
 */
const AnimatedNumber: React.FC<{ from: number; to: number; duration?: number }> = ({
    from,
    to,
    duration = 700,
}) => {
    const [value, setValue] = useState(from);
    useEffect(() => {
        const reduced = typeof window !== 'undefined'
            && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (reduced || from === to) {
            setValue(to);
            return;
        }
        const start = performance.now();
        let raf = 0;
        const step = (now: number) => {
            const t = Math.min(1, (now - start) / duration);
            // ease-out cubic — feels like the number is "settling"
            const eased = 1 - Math.pow(1 - t, 3);
            setValue(Math.round(from + (to - from) * eased));
            if (t < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [from, to, duration]);
    return <>{value}</>;
};

const WordPracticeModal: React.FC<WordPracticeModalProps> = ({
    word,
    originalScore,
    phonetic,
    audioUrl,
    isRecording,
    isAssessing,
    isPlayingAudio,
    result,
    onStartRecord,
    onStopRecord,
    onPlayAudio,
    onRetry,
    onCancel,
}) => {
    const { t } = useTranslation('learning');
    const dialogRef = useRef<HTMLDivElement>(null);

    // Lock body scroll while the modal is open.
    useEffect(() => {
        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = original;
        };
    }, []);

    // ESC dismisses, but only when the assessor isn't mid-flight —
    // dismissing while the score is being computed would orphan the result.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isAssessing) {
                onCancel();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isAssessing, onCancel]);

    // Send focus into the dialog on mount so the surrounding page's
    // keyboard handlers stop intercepting Enter / Space.
    useEffect(() => {
        dialogRef.current?.focus();
    }, []);

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && !isAssessing) {
            onCancel();
        }
    };

    const title = t('shadowing.wordPractice.title', { defaultValue: 'Word Practice' });
    const showResult = !!result;

    return (
        <div
            className="word-practice-modal__backdrop"
            onClick={handleBackdropClick}
            role="presentation"
        >
            <div
                ref={dialogRef}
                className="word-practice-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="word-practice-modal-title"
                tabIndex={-1}
            >
                <button
                    type="button"
                    className="word-practice-modal__close"
                    onClick={onCancel}
                    disabled={isAssessing}
                    aria-label={t('shadowing.wordPractice.cancel', { defaultValue: 'Cancel' })}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                    </svg>
                </button>

                <div className="word-practice-modal__header">
                    <div className="word-practice-modal__title-row">
                        <h4 id="word-practice-modal-title" className="word-practice-modal__title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <circle cx="12" cy="12" r="6" />
                                <circle cx="12" cy="12" r="2" />
                            </svg>
                            <span>{title}</span>
                        </h4>
                        {!showResult && (
                            <span className="word-practice-modal__original-score">
                                {t('shadowing.wordPractice.originalScore', {
                                    score: Math.round(originalScore),
                                    defaultValue: 'Original: {{score}}',
                                })}
                            </span>
                        )}
                    </div>

                    <div className="word-practice-modal__word-row">
                        <strong className="word-practice-modal__word">{word}</strong>
                        {phonetic && (
                            <span className="word-practice-modal__phonetic">/{phonetic}/</span>
                        )}
                        {audioUrl && (
                            <button
                                type="button"
                                className={`word-practice-modal__play${isPlayingAudio ? ' word-practice-modal__play--playing' : ''}`}
                                onClick={onPlayAudio}
                                aria-label="Play reference audio"
                            >
                                <span className="word-practice-modal__play-icon">{isPlayingAudio ? '■' : '▶'}</span>
                            </button>
                        )}
                    </div>
                </div>

                {showResult ? (
                    <div className="word-practice-modal__result">
                        {(() => {
                            // Highlight + Loopy mascot only when the word
                            // actually improved — celebration belongs to
                            // wins, not flat / regressed retries.
                            const wordImproved = Math.round(result.wordAfter) > Math.round(result.wordBefore);
                            return (
                                <ScoreDelta
                                    label={t('shadowing.wordPractice.thisWord', { defaultValue: '該單字' })}
                                    from={result.wordBefore}
                                    to={result.wordAfter}
                                    highlight={wordImproved}
                                    celebrate={wordImproved}
                                />
                            );
                        })()}
                        {typeof result.accuracyBefore === 'number' && typeof result.accuracyAfter === 'number' && (
                            <ScoreDelta
                                label={t('shadowing.wordPractice.accuracy', { defaultValue: '準確度' })}
                                from={result.accuracyBefore}
                                to={result.accuracyAfter}
                            />
                        )}
                        {typeof result.overallBefore === 'number' && typeof result.overallAfter === 'number' && (
                            <ScoreDelta
                                label={t('shadowing.wordPractice.overall', { defaultValue: '整體' })}
                                from={result.overallBefore}
                                to={result.overallAfter}
                            />
                        )}

                        <div className="word-practice-modal__result-actions">
                            <button
                                type="button"
                                className="word-practice-modal__btn word-practice-modal__btn--secondary"
                                onClick={onRetry}
                            >
                                {t('shadowing.wordPractice.retryAgain', { defaultValue: '再試一次' })}
                            </button>
                            <button
                                type="button"
                                className="word-practice-modal__btn word-practice-modal__btn--primary"
                                onClick={onCancel}
                                autoFocus
                            >
                                {t('shadowing.wordPractice.done', { defaultValue: '完成' })}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="word-practice-modal__controls">
                        {isAssessing ? (
                            <div className="word-practice-modal__assessing">
                                <div className="word-practice-modal__spinner" />
                                <span>{t('shadowing.recording.assessing', { defaultValue: 'Analyzing⋯' })}</span>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className={`word-practice-modal__record${isRecording ? ' word-practice-modal__record--recording' : ''}`}
                                onClick={isRecording ? onStopRecord : onStartRecord}
                            >
                                {isRecording ? (
                                    <>
                                        <span className="word-practice-modal__rec-dot" />
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="3" width="18" height="18" rx="2" />
                                        </svg>
                                        <span>{t('shadowing.recording.stop', { defaultValue: 'Stop' })}</span>
                                    </>
                                ) : (
                                    <>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="9" y="2" width="6" height="12" rx="3" />
                                            <path d="M5 10a7 7 0 0 0 14 0" />
                                            <line x1="12" y1="19" x2="12" y2="22" />
                                        </svg>
                                        <span>{t('shadowing.wordPractice.record', {
                                            word,
                                            defaultValue: 'Record "{{word}}"',
                                        })}</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const ScoreDelta: React.FC<{
    label: string;
    from: number;
    to: number;
    highlight?: boolean;
    /** When true, prepends a celebrating Loopy avatar — used for the
     *  headline word row when the score actually improved. */
    celebrate?: boolean;
}> = ({ label, from, to, highlight, celebrate }) => {
    const delta = Math.round(to) - Math.round(from);
    const direction = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
    // Pick a celebration emotion based on how much the word improved.
    // Bigger jumps get the more enthusiastic mascot.
    const emotion: LoopyEmotion = delta >= 30 ? 'celebrate' : delta >= 10 ? 'yeah' : 'wink';
    return (
        <div className={`word-practice-modal__delta${highlight ? ' word-practice-modal__delta--highlight' : ''}`}>
            <span className="word-practice-modal__delta-label">
                {celebrate && (
                    <LoopyAvatar size="sm" emotion={emotion} ariaLabel="" />
                )}
                <span>{label}</span>
            </span>
            <span className="word-practice-modal__delta-bar">
                <span className="word-practice-modal__delta-from">{Math.round(from)}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                </svg>
                <span className={`word-practice-modal__delta-to word-practice-modal__delta-to--${direction}`}>
                    <AnimatedNumber from={Math.round(from)} to={Math.round(to)} />
                </span>
                {direction === 'up' && (
                    <span className="word-practice-modal__delta-badge word-practice-modal__delta-badge--up">+{delta}</span>
                )}
                {direction === 'down' && (
                    <span className="word-practice-modal__delta-badge word-practice-modal__delta-badge--down">{delta}</span>
                )}
            </span>
        </div>
    );
};

export default WordPracticeModal;
